// ABOUTME: Report detail page with editor interface.
// ABOUTME: Shows ReportWizard for new reports and ReportEditor for existing ones.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ReportWizard, ReportEditor } from '@/components/report';
import { apiClient, ApiError } from '@/services/api';
import type { Report } from '@/types/api';

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNewReport = id === 'new';

  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(!isNewReport);
  const [error, setError] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Fetch existing report
  useEffect(() => {
    if (isNewReport || !id) return;

    const fetchReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const fetchedReport = await apiClient.reports.get(id);
        setReport(fetchedReport);
      } catch (err) {
        console.error('Failed to fetch report:', err);
        if (err instanceof ApiError && err.status === 404) {
          setError('Report not found');
        } else {
          setError('Failed to load report. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [id, isNewReport]);

  // Handle wizard completion
  const handleWizardComplete = (newReport: Report) => {
    setReport(newReport);
    // Update URL to the new report ID without adding to history
    navigate(`/reports/${newReport.id}`, { replace: true });
  };

  // Handle report save
  const handleSave = (updatedReport: Report) => {
    setReport(updatedReport);
  };

  // Handle regenerate — enter wizard in edit mode
  const handleRegenerate = () => {
    setIsRegenerating(true);
  };

  // Handle regeneration complete — return to editor with updated report
  const handleRegenerateComplete = (updatedReport: Report) => {
    setReport(updatedReport);
    setIsRegenerating(false);
  };

  // Handle navigation back to reports list
  const handleBack = () => {
    navigate('/reports');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-sm text-surface-600">Loading report...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Card variant="surface" className="max-w-md p-8 text-center">
          <div className="w-16 h-16 rounded-sm bg-error-light flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-error" />
          </div>
          <h2 className="text-xl font-semibold text-surface-800 mb-2">
            {error === 'Report not found' ? 'Report Not Found' : 'Error Loading Report'}
          </h2>
          <p className="text-sm text-surface-600 mb-6">
            {error === 'Report not found'
              ? 'The report you are looking for does not exist or has been deleted.'
              : error}
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="ghost" onClick={handleBack}>
              Back to Reports
            </Button>
            {error !== 'Report not found' && (
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // New report wizard
  if (isNewReport && !report) {
    return (
      <div className="h-full">
        <ReportWizard
          onComplete={handleWizardComplete}
          onCancel={handleBack}
        />
      </div>
    );
  }

  // Regenerate mode — show wizard pre-filled with existing config
  if (isRegenerating && report) {
    return (
      <div className="h-full">
        <ReportWizard
          editReportId={report.id}
          editReportVersion={report.version}
          onComplete={handleRegenerateComplete}
          onCancel={() => setIsRegenerating(false)}
        />
      </div>
    );
  }

  // Report editor
  if (report) {
    return (
      <div className="h-full">
        <ReportEditor
          report={report}
          onSave={handleSave}
          onBack={handleBack}
          onRegenerate={handleRegenerate}
        />
      </div>
    );
  }

  // Fallback (should not reach here)
  return null;
}
